import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { useCallbackRef } from "./useCallbackRef";

/**
 * Manages state that can be either controlled (value provided via prop) or
 * uncontrolled (value managed internally). Follows the Radix controllable-state pattern.
 *
 * @see https://github.com/radix-ui/primitives/blob/main/packages/react/use-controllable-state/src/useControllableState.tsx
 */

type UseControllableStateParams<T> = {
  prop?: T | undefined;
  defaultProp?: T | undefined;
  onChange?: (state: T) => void;
};
type SetStateFn<T> = (prevState?: T) => T;
function useUncontrolledState(t0) {
  const $ = _c(4);
  const {
    defaultProp,
    onChange
  } = t0;
  const uncontrolledState = useState(defaultProp);
  const [value] = uncontrolledState;
  const prevValueRef = useRef(value);
  const handleChange = useCallbackRef(onChange);
  let t1;
  let t2;
  if ($[0] !== handleChange || $[1] !== value) {
    t1 = () => {
      if (prevValueRef.current !== value) {
        handleChange(value as T);
        prevValueRef.current = value;
      }
    };
    t2 = [value, prevValueRef, handleChange];
    $[0] = handleChange;
    $[1] = value;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return uncontrolledState;
}
export function useControllableState(t0) {
  const $ = _c(11);
  const {
    prop,
    defaultProp,
    onChange: t1
  } = t0;
  const onChange = t1 === undefined ? _temp : t1;
  let t2;
  if ($[0] !== defaultProp || $[1] !== onChange) {
    t2 = {
      defaultProp,
      onChange
    };
    $[0] = defaultProp;
    $[1] = onChange;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [uncontrolledProp, setUncontrolledProp] = useUncontrolledState(t2);
  const isControlled = prop !== undefined;
  const value = isControlled ? prop : uncontrolledProp;
  const handleChange = useCallbackRef(onChange);
  let t3;
  if ($[3] !== handleChange || $[4] !== isControlled || $[5] !== prop || $[6] !== setUncontrolledProp) {
    t3 = nextValue => {
      if (isControlled) {
        const setter = nextValue as SetStateFn<T>;
        const value_0 = typeof nextValue === "function" ? setter(prop) : nextValue;
        if (value_0 !== prop) {
          handleChange(value_0 as T);
        }
      } else {
        setUncontrolledProp(nextValue);
      }
    };
    $[3] = handleChange;
    $[4] = isControlled;
    $[5] = prop;
    $[6] = setUncontrolledProp;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const setValue = t3;
  let t4;
  if ($[8] !== setValue || $[9] !== value) {
    t4 = [value, setValue];
    $[8] = setValue;
    $[9] = value;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4 as const;
}
function _temp() {}